import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import Drawer from '../../ui/Drawer'
import Tabs from '../../ui/Tabs'
import Loader from '../../shared/Loader'
import { showToast } from '../../shared/Toast'
import useIssueDetail from '../hooks/useIssueDetail'
import useAssignableUsers from '../hooks/useAssignableUsers'
import useIssueStatuses from '../hooks/useIssueStatuses'
import IssuesService from '../../../services/api/IssuesService'
import DiscardChangesModal from '../IssueStatuses/DiscardChangesModal'
import IssueDrawerHeader from './IssueDrawerHeader'
import IssueDrawerFooter from './IssueDrawerFooter'
import IssueSummaryTab from './IssueSummaryTab'
import IssueResponseTab from './IssueResponseTab'
import ShareIssueModal from './ShareIssueModal'
import groupUsersByRole from '../shared/groupUsersByRole'
import { TOAST_CONTAINER_ID } from './constants'

const FULL_NAME_KEY = 'full_name'
const USER_IDS_KEY = 'user_ids'
const REMOVED_PHOTO_IDS_KEY = 'removed_photo_ids'
const ISSUE_STATUS_ID_KEY = 'issue_status_id'
const STATUS_ID_KEY = 'status_id'
const HAS_ADDITIONAL_ANSWER = 'has_additional_answer'
const ADDITIONAL_RESULTS = 'additional_results'
const TRIGGERED_GLOBAL_GRADE = 'triggered_global_grade'

const ERROR_MSG = 'Une erreur est survenue lors de la modification de l’alerte.'
const SUCCESS_MSG = 'La modification de l’alerte a bien été effectuée.'
const DISCARD_MSG = 'Les modifications apportées sur l’alerte ne seront pas sauvegardées.'
const NO_ADDITIONAL_MSG = 'Pas de contrôle supplémentaire réalisé.'

// The rule sets the alert-management tab apart from the read-only answer tabs.
const TABS = [
  { value: 'summary', label: 'Résumé' },
  { value: 'response', label: 'Détails de la réponse', dividerBefore: true },
  { value: 'additional', label: 'Contrôle supplémentaire' }
]

const TABS_WRAP_CLASS = 'tw:pb-4'
// The drawer packs its tabs tighter than the shared component's default.
const TABS_GAP_CLASS = 'tw:gap-4!'
const PLACEHOLDER_CLASS = 'tw:text-sm tw:text-gray-500 tw:py-8 tw:text-center'
// Figma "Content Body" (2153:3892) grounds the tabs as well as the cards, not Drawer.Body alone.
const BODY_CLASS = 'tw:flex-1 tw:overflow-y-auto tw:bg-gray-50 tw:p-6'

// Filtering by options would drop attributees absent from the assignable list (hidden staff, removed user).
const buildSelectedUsers = (attribution, options) => {
  const optionByValue = new Map(options.map(option => [option.value, option]))
  return attribution.map(user => optionByValue.get(user.value) || user)
}

const sameUserIds = (left, right) => {
  if (left.length !== right.length)
    return false
  const ids = new Set(right.map(option => option.value))
  return left.every(option => ids.has(option.value))
}

const EditIssueDrawer = ({
  collectId, issueId = null, initialIssue = null, isOpen, onClose, onUpdated = null
}) => {
  const { issue, loading, error } = useIssueDetail(collectId, issueId, { enabled: isOpen, initialIssue })
  const userOptions = useAssignableUsers(collectId, isOpen, TOAST_CONTAINER_ID)
  // This issue's own collect: the shared prop carries the merged step union, keyed by name, not ids.
  const { statuses: issueStatuses } = useIssueStatuses(isOpen ? collectId : null)
  const recipientOptions = useMemo(() => groupUsersByRole(userOptions), [userOptions])
  const [activeTab, setActiveTab] = useState('summary')
  const [openSectionIds, setOpenSectionIds] = useState({})
  const [isEditing, setIsEditing] = useState(false)
  const [statusValue, setStatusValue] = useState(null)
  const [commentValue, setCommentValue] = useState('')
  const [selectedUsers, setSelectedUsers] = useState([])
  const [newPhotos, setNewPhotos] = useState([])
  const [removedPhotoIds, setRemovedPhotoIds] = useState([])
  const [saving, setSaving] = useState(false)
  const [discarding, setDiscarding] = useState(false)
  const [sharing, setSharing] = useState(false)
  // Prevent a late userOptions fetch from clobbering an in-progress edit.
  const usersTouched = useRef(false)
  const initialUsers = useRef([])

  useEffect(() => {
    setActiveTab('summary')
    setOpenSectionIds({})
    setIsEditing(false)
  }, [issueId])

  useEffect(() => {
    usersTouched.current = false
    setNewPhotos([])
    setRemovedPhotoIds([])
    if (!issue) {
      setStatusValue(null)
      setCommentValue('')
      setSelectedUsers([])
      return
    }
    setStatusValue(issue[STATUS_ID_KEY])
    setCommentValue(issue.comment || '')
  }, [issue])

  useEffect(() => {
    if (!issue || usersTouched.current)
      return
    const attribution = (issue.attribution || []).map(user => ({
      id: user.id, value: user.id, label: user[FULL_NAME_KEY]
    }))
    const enriched = buildSelectedUsers(attribution, userOptions)
    initialUsers.current = enriched
    setSelectedUsers(enriched)
  }, [issue, userOptions])

  const isDirty = Boolean(issue) && (
    statusValue !== issue[STATUS_ID_KEY]
    || commentValue !== (issue.comment || '')
    || newPhotos.length > 0
    || removedPhotoIds.length > 0
    || !sameUserIds(selectedUsers, initialUsers.current)
  )

  const handleStatusChange = useCallback(statusId => setStatusValue(Number(statusId)), [])
  const handleCommentChange = useCallback(event => setCommentValue(event.target.value), [])
  const handleEdit = useCallback(() => setIsEditing(true), [])
  const handleUsersChange = useCallback(value => {
    usersTouched.current = true
    setSelectedUsers(value || [])
  }, [])

  const handleAddPhotos = useCallback(files => {
    setNewPhotos(previous => [...previous, ...files])
  }, [])
  const handleRemoveExisting = useCallback(signedId => {
    setRemovedPhotoIds(previous => [...previous, signedId])
  }, [])
  const handleRemoveNew = useCallback(index => {
    setNewPhotos(previous => previous.filter((unused, position) => position !== index))
  }, [])

  const handleClose = useCallback(() => {
    if (saving)
      return
    if (isDirty) {
      setDiscarding(true)
      return
    }
    onClose()
  }, [saving, isDirty, onClose])

  const handleKeepEditing = useCallback(() => setDiscarding(false), [])
  const handleConfirmDiscard = useCallback(() => {
    setDiscarding(false)
    onClose()
  }, [onClose])

  const handleOpenSectionIdsChange = useCallback(ids => {
    setOpenSectionIds(previous => ({ ...previous, [activeTab]: ids }))
  }, [activeTab])

  const handleShare = useCallback(() => setSharing(true), [])
  const handleShareClose = useCallback(() => setSharing(false), [])

  const handleSave = useCallback(async() => {
    if (!issueId || saving)
      return
    setSaving(true)
    try {
      await IssuesService.updateIssue(collectId, issueId, {
        [ISSUE_STATUS_ID_KEY]: statusValue,
        comment: commentValue,
        [USER_IDS_KEY]: selectedUsers.map(option => option.value),
        photos: newPhotos,
        [REMOVED_PHOTO_IDS_KEY]: removedPhotoIds
      })
      showToast(SUCCESS_MSG, 'success', TOAST_CONTAINER_ID)
      if (onUpdated)
        onUpdated()
      onClose()
    } catch (caught) {
      showToast(caught?.message || ERROR_MSG, 'error', TOAST_CONTAINER_ID)
    } finally {
      setSaving(false)
    }
  }, [
    collectId,
    issueId,
    saving,
    statusValue,
    commentValue,
    selectedUsers,
    newPhotos,
    removedPhotoIds,
    onUpdated,
    onClose
  ])

  const renderResponseTab = (results, globalGrade = null) => (
    <IssueResponseTab
      key={activeTab}
      globalGrade={globalGrade}
      openIds={openSectionIds[activeTab]}
      results={results}
      onOpenIdsChange={handleOpenSectionIdsChange}
    />
  )

  const renderAdditionalTab = () => {
    if (!issue.answer?.has_additional)
      return <p className={PLACEHOLDER_CLASS}>{NO_ADDITIONAL_MSG}</p>
    return renderResponseTab(issue[ADDITIONAL_RESULTS])
  }

  const renderTabContent = () => {
    if (activeTab === 'response')
      return renderResponseTab(issue.results, issue[TRIGGERED_GLOBAL_GRADE])
    if (activeTab === 'additional')
      return renderAdditionalTab()
    return (
      <IssueSummaryTab
        commentValue={commentValue}
        isEditing={isEditing}
        issue={issue}
        issueStatuses={issueStatuses}
        newPhotos={newPhotos}
        recipientOptions={recipientOptions}
        removedPhotoIds={removedPhotoIds}
        selectedUsers={selectedUsers}
        statusValue={statusValue}
        onAddPhotos={handleAddPhotos}
        onCommentChange={handleCommentChange}
        onEdit={handleEdit}
        onRemoveExisting={handleRemoveExisting}
        onRemoveNew={handleRemoveNew}
        onStatusChange={handleStatusChange}
        onUsersChange={handleUsersChange}
      />
    )
  }

  const renderContent = () => {
    // Drawer stays mounted for the slide animation; render nothing when closed.
    if (!issueId)
      return null
    if (loading && !issue)
      return <Drawer.Body><div className="tw:flex tw:justify-center"><Loader /></div></Drawer.Body>
    if (error || !issue)
      return <Drawer.Body><p className={PLACEHOLDER_CLASS}>{ERROR_MSG}</p></Drawer.Body>
    return (
      <>
        <IssueDrawerHeader
          issue={issue}
          onClose={handleClose}
          onShare={handleShare}
        />
        <div className={BODY_CLASS}>
          <div className={TABS_WRAP_CLASS}>
            <Tabs
              className={TABS_GAP_CLASS}
              items={TABS.filter(tab => tab.value !== 'additional' || issue.collect?.[HAS_ADDITIONAL_ANSWER])}
              value={activeTab}
              variant="neutral"
              onChange={setActiveTab}
            />
          </div>
          {renderTabContent()}
        </div>
        <IssueDrawerFooter
          isDirty={isDirty}
          saving={saving}
          onCancel={handleClose}
          onSave={handleSave}
        />
        <DiscardChangesModal
          isOpen={discarding}
          message={DISCARD_MSG}
          onCancel={handleKeepEditing}
          onConfirm={handleConfirmDiscard}
        />
        <ShareIssueModal
          collectId={collectId}
          isOpen={sharing}
          issueId={issue.id}
          onClose={handleShareClose}
        />
      </>
    )
  }

  return (
    <Drawer
      ariaLabel="Gestion de l’alerte"
      closeOnBackdrop={!saving}
      closeOnEscape={!saving}
      isOpen={isOpen}
      onClose={handleClose}
    >
      {renderContent()}
    </Drawer>
  )
}

EditIssueDrawer.propTypes = {
  collectId: PropTypes.number.isRequired,
  initialIssue: PropTypes.shape({ id: PropTypes.number }),
  isOpen: PropTypes.bool.isRequired,
  issueId: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  onUpdated: PropTypes.func
}

EditIssueDrawer.defaultProps = {
  initialIssue: null,
  issueId: null,
  onUpdated: null
}

export default EditIssueDrawer
